import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

/**
 * Standalone, reusable modal editor for a user's `custom:notify_channels`
 * value — a FLAT CSV of channel ids (e.g. "as-1,as-7,as-12") with a
 * visual scanner-grouped UI on top.
 *
 *   <app-channels-editor
 *     *ngIf="showChannelsEditor"
 *     [customers]="customers"
 *     [scanners]="scanners"
 *     [userScanners]="user.attributes['custom:scanners']"
 *     [channelsJson]="user.attributes['custom:notify_channels']"  <-- CSV today
 *     [title]="'Edit Channels'"
 *     (save)="onSaveChannels($event)"
 *     (close)="showChannelsEditor = false"
 *   ></app-channels-editor>
 *
 * Storage shape: flat CSV — kept compatible with the fleet's existing
 * CSV readers (alpha-shark-push-service user-management, f2-admin-service
 * api-key channel lookup + login tracking, the grid's CSV cell renderer).
 * A per-scanner JSON shape was considered but the migration would silently
 * corrupt those consumers; option deferred until alpha-shark migrates to
 * Cognito and those services are refactored off direct-Cognito reads.
 *
 * Visual UI: one card per scanner the user is entitled to via
 * custom:scanners, with the scanner's Scanners.<id>.channels[] meta
 * supplying the checkbox set per card. On save, picked channel ids
 * across ALL cards are unioned and emitted as a deduped CSV.
 *
 * Orphan ids (in the CSV but not declared on any entitled scanner)
 * surface as a tail "Other channels" chip row so the admin can review
 * + prune rather than silently dropping them on save.
 */

interface ChannelDef {
  id: string;
  slug?: string;
  title: string;
  // IT-F2-406 cid d97cf968 — optional role-visibility gate on each
  // channel definition. absent/empty → visible to all entitled;
  // non-empty → user's custom:role must be in list; admins bypass.
  // Shape defined by f2-session c/2d600d1a (Scanners.<id>.channels[].visible_to_roles).
  visible_to_roles?: string[];
}

interface CustomerLite {
  _id: string;
  name?: string;
  scanner_ids?: string[];
}

interface ScannerLite {
  _id?: string;
  Id?: string;
  Name?: string;
  Client?: string;
  channels?: ChannelDef[];
  // IT-F2-391 cid 445a57ad — NC alert channels on a scanner can live on
  // the sibling `channel_templates` map (keyed by channel-key) when the
  // scanner is driven by the Notification-Center path rather than the
  // fleet push-channel schema (`channels[]`). When present and `channels[]`
  // is empty, we synthesize channel entries from the template keys so
  // the admin Users panel can edit `custom:notify_channels` for them.
  channel_templates?: Record<string, { enabled?: boolean; body_html_template?: string; schedule?: any }>;
}

interface ChannelsCard {
  scannerId: string;
  scannerName: string;
  customer: CustomerLite;
  channels: ChannelDef[];     // available channels on this scanner
  selected: Set<string>;      // channel ids currently checked (per card)
  expanded: boolean;          // UX only — header chevron toggle
  // IT-F2-406 cid d97cf968 — per-channel visible_to_roles edits that
  // deviate from the server-side value. Keyed by the ChannelDef object
  // reference so the mapping survives id-edits (cid df79e959 made id
  // editable; object identity is stable across string-field mutations).
  // Only populated when the admin actually toggles a chip.
  roleOverrides: Map<ChannelDef, Set<string>>;
  // IT-F2-406 cid df79e959 — dirty flag flipped by every title/id/slug
  // input change. Independent of roleOverrides so edits to metadata
  // alone still trigger a scanner PATCH on Save.
  metadataDirty: boolean;
}

export interface ScannerChannelsUpdate {
  scanner_id: string;
  channels: ChannelDef[];
}

const ROLE_OPTIONS: ReadonlyArray<string> = ['admin', 'client_admin', 'client', 'member'];
const ROLE_LABELS: Record<string, string> = {
  admin: 'ADM',
  client_admin: 'C-ADM',
  client: 'CLI',
  member: 'MEM',
};

@Component({
  standalone: true,
  selector: 'app-channels-editor',
  imports: [CommonModule, FormsModule],
  templateUrl: './channels-editor.component.html',
})
export class ChannelsEditorComponent implements OnChanges {
  @Input() customers: CustomerLite[] = [];
  @Input() scanners: ScannerLite[] = [];
  @Input() userScanners: string = '';
  // Target user's custom:role — when 'admin', the entitlement filter is
  // bypassed and every scanner in the catalog renders as a card. Mike
  // 2026-07-06 (memory feedback_admin_customers_scanners_unfiltered_by_pool).
  @Input() userRole: string = '';
  // CSV today (e.g. "as-1,as-7,as-12"). Compatible with existing
  // fleet-wide readers — see component docstring for rationale.
  @Input() channelsJson: string = '';
  @Input() title: string = 'Edit Channels';
  @Input() userDisplayName: string = '';
  @Input() userEmail: string = '';
  @Input() userPoolId: string = '';

  @Output() save = new EventEmitter<string>();
  // IT-F2-406 cid d97cf968 — emitted on save when the admin touched any
  // channel's visible_to_roles chips. One entry per scanner whose channels[]
  // changed; `channels` is the full merged array (unchanged entries carry
  // their server-side visible_to_roles verbatim). Consumer PATCHes each
  // scanner via scannersService.updateScanner(scanner_id, { channels }).
  @Output() saveScannerChannels = new EventEmitter<ScannerChannelsUpdate[]>();
  @Output() close = new EventEmitter<void>();

  readonly ROLE_OPTIONS = ROLE_OPTIONS;
  readonly ROLE_LABELS = ROLE_LABELS;

  cards: ChannelsCard[] = [];
  cardGroups: { customer: CustomerLite; cards: ChannelsCard[] }[] = [];
  showDebugModal: boolean = false;
  debugCopied: boolean = false;
  // Channel ids present in the user's CSV that don't belong to any
  // entitled scanner's channels[] meta. Rendered as pills with a remove
  // button; surviving orphans round-trip on save unless removed.
  orphans: string[] = [];

  private _scannerToCustomer = new Map<string, string>();
  private _customerById = new Map<string, CustomerLite>();

  ngOnChanges(_: SimpleChanges) {
    this._rebuild_lookups();
    this._parse_channels();
  }

  private _rebuild_lookups() {
    this._scannerToCustomer.clear();
    this._customerById.clear();
    for (const c of (this.customers || [])) {
      this._customerById.set(c._id, c);
      for (const sid of (c.scanner_ids || [])) this._scannerToCustomer.set(sid, c._id);
    }
    for (const s of (this.scanners || [])) {
      const id = String(s._id || s.Id || '');
      const client = String(s.Client || '');
      if (id && client && !this._scannerToCustomer.has(id)) {
        this._scannerToCustomer.set(id, client);
      }
    }
  }

  // Tokens from custom:scanners — supports literals AND wildcards
  // ('tt-*') per the standing wildcard contract.
  private _isAdminTarget(): boolean {
    return String(this.userRole || '').trim().toLowerCase() === 'admin';
  }

  private _entitledScannerIds(): string[] {
    // Admin-role targets see every scanner regardless of their
    // custom:scanners CSV — their role grants fleet-wide access.
    if (this._isAdminTarget()) {
      const all = new Set<string>();
      for (const s of (this.scanners || [])) {
        const id = String(s._id || s.Id || '');
        if (id) all.add(id);
      }
      return Array.from(all);
    }
    const tokens: string[] = [];
    const seen = new Set<string>();
    for (const s of String(this.userScanners || '').split(',')) {
      const t = s.trim();
      if (t && !seen.has(t)) { seen.add(t); tokens.push(t); }
    }
    const literals = new Set<string>();
    const prefixes: string[] = [];
    for (const t of tokens) {
      if (t.endsWith('*')) prefixes.push(t.slice(0, -1));
      else literals.add(t);
    }
    const matched = new Set<string>();
    for (const s of (this.scanners || [])) {
      const id = String(s._id || s.Id || '');
      if (!id) continue;
      if (literals.has(id)) { matched.add(id); continue; }
      for (const p of prefixes) {
        if (id.startsWith(p)) { matched.add(id); break; }
      }
    }
    for (const l of literals) matched.add(l);

    // ── Widen entitlement for CARD RENDERING only ─────────────────────
    // The strict custom:scanners boundary above works for URL routing
    // but is too narrow for the channels editor. Alpha-shark declares
    // its 27 push channels on a channels-only scanner (`as-push`) that
    // isn't in most users' custom:scanners — product users get e.g.
    // `as-os` / `alpha-shark-flow`. Without widening, every real
    // as-N channel Mike assigns lands as an opaque orphan pill.
    //
    // Widen by:
    //  (A) sibling scanners under the same `Client` as any matched
    //      scanner — if the user has ANY alpha-shark entitlement, the
    //      as-push channel card shows up so Mike can add/remove push
    //      channels alongside the product access already granted.
    //  (B) any scanner whose channels[] intersects the user's current
    //      notify_channels CSV — surfaces the owning scanner so orphans
    //      can be pruned per-channel rather than as a wall of chips.

    // (A) Same-Client sibling scanners.
    const entitledClients = new Set<string>();
    for (const s of (this.scanners || [])) {
      const id = String(s._id || s.Id || '');
      if (id && matched.has(id)) {
        const client = String(s.Client || '').trim();
        if (client) entitledClients.add(client);
      }
    }
    if (entitledClients.size > 0) {
      for (const s of (this.scanners || [])) {
        const id = String(s._id || s.Id || '');
        const client = String(s.Client || '').trim();
        if (id && client && entitledClients.has(client)) matched.add(id);
      }
    }

    // (B) Scanners declaring one of the user's currently-assigned channels.
    // Also honor sibling `channel_templates` keys here (IT-F2-391 cid
    // 445a57ad) so NC-backed scanners surface even when the legacy
    // channels[] array is empty on the scanner doc.
    const userChannelIds = new Set(
      String(this.channelsJson || '').split(/[,;\s]+/).map(s => s.trim()).filter(Boolean)
    );
    if (userChannelIds.size > 0) {
      for (const s of (this.scanners || [])) {
        const id = String(s._id || s.Id || '');
        if (!id || matched.has(id)) continue;
        const chans = Array.isArray(s.channels) ? s.channels : [];
        let hit = false;
        for (const ch of chans) {
          if (userChannelIds.has(String(ch.id))) { hit = true; break; }
        }
        if (!hit && s.channel_templates && typeof s.channel_templates === 'object') {
          for (const k of Object.keys(s.channel_templates)) {
            if (userChannelIds.has(String(k || '').trim())) { hit = true; break; }
          }
        }
        if (hit) matched.add(id);
      }
    }

    return Array.from(matched);
  }

  private _parse_channels() {
    this.cards = [];
    this.cardGroups = [];
    this.orphans = [];

    // Build per-scanner cards from the user's entitlements.
    const scannerById = new Map<string, ScannerLite>();
    for (const s of (this.scanners || [])) {
      const id = String(s._id || s.Id || '');
      if (id) scannerById.set(id, s);
    }
    const cardByChannelId = new Map<string, ChannelsCard>();
    for (const scannerId of this._entitledScannerIds()) {
      const scanner = scannerById.get(scannerId);
      const customerSlug = this._scannerToCustomer.get(scannerId) || '';
      // Synthesize a minimal customer stub when the full directory
      // isn't available (non-admin caller — /admin/customer-directory
      // is super-admin only). Same rationale as claims-editor. Mike
      // 2026-07-02.
      const customer = customerSlug
        ? (this._customerById.get(customerSlug) || { _id: customerSlug, name: customerSlug } as CustomerLite)
        : undefined;
      let channels: ChannelDef[] | undefined = scanner?.channels;
      // IT-F2-391 cid 445a57ad (Mike 2026-10-23) — NC alert channels
      // on a scanner may live on sibling `channel_templates` instead of
      // the legacy `channels[]` push-schema. When `channels[]` is empty,
      // synthesize entries from the template-map keys so admins can
      // still edit `custom:notify_channels` from the Users panel.
      // Template keys are snake_case ids (e.g. `entry_alerts`); derive
      // a Title Case display name for the picker.
      if (!Array.isArray(channels) || channels.length === 0) {
        const tplMap = scanner?.channel_templates;
        if (tplMap && typeof tplMap === 'object') {
          const synth: ChannelDef[] = [];
          for (const key of Object.keys(tplMap)) {
            const trimmed = String(key || '').trim();
            if (!trimmed) continue;
            const title = trimmed
              .split(/[_\-\s]+/)
              .filter(Boolean)
              .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
              .join(' ');
            synth.push({ id: trimmed, title: title || trimmed });
          }
          if (synth.length) channels = synth;
        }
      }
      if (!customer || !Array.isArray(channels) || channels.length === 0) continue;
      // IT-F2-406 cid df79e959 — deep-copy channels[] so inline-edited
      // id/title/slug/visible_to_roles don't mutate the shared ScannerLite
      // instance in this.scanners before Save fires scannersService.updateScanner.
      const localChannels: ChannelDef[] = channels.map(c => ({ ...c }));
      const card: ChannelsCard = {
        scannerId,
        scannerName: String(scanner?.Name || ''),
        customer,
        channels: localChannels,
        selected: new Set<string>(),
        expanded: true,
        roleOverrides: new Map<ChannelDef, Set<string>>(),
        metadataDirty: false,
      };
      this.cards.push(card);
      for (const ch of localChannels) cardByChannelId.set(ch.id, card);
    }
    this.cards.sort((a, b) => {
      const an = String(a.customer.name || a.customer._id).toLowerCase();
      const bn = String(b.customer.name || b.customer._id).toLowerCase();
      const c = an.localeCompare(bn);
      return c !== 0 ? c : a.scannerId.localeCompare(b.scannerId);
    });

    // Bucket per-customer for the outer box rendering.
    let currentSlug = '';
    let bucket: { customer: CustomerLite; cards: ChannelsCard[] } | null = null;
    for (const c of this.cards) {
      if (c.customer._id !== currentSlug) {
        currentSlug = c.customer._id;
        bucket = { customer: c.customer, cards: [] };
        this.cardGroups.push(bucket);
      }
      bucket!.cards.push(c);
    }

    // Hydrate from existing CSV. Tokens that match a card's channel
    // get checked there; the rest become orphans.
    const raw = String(this.channelsJson || '').trim();
    if (!raw) return;
    const ids = raw.split(/[,;\s]+/).map(s => s.trim()).filter(Boolean);
    const seen = new Set<string>();
    for (const id of ids) {
      if (seen.has(id)) continue;
      seen.add(id);
      const card = cardByChannelId.get(id);
      if (card) card.selected.add(id);
      else this.orphans.push(id);
    }
  }

  toggleExpanded(card: ChannelsCard) {
    card.expanded = !card.expanded;
  }

  toggleChannel(card: ChannelsCard, channelId: string) {
    if (card.selected.has(channelId)) card.selected.delete(channelId);
    else card.selected.add(channelId);
  }

  isChannelSelected(card: ChannelsCard, channelId: string): boolean {
    return card.selected.has(channelId);
  }

  // IT-F2-406 cid d97cf968 — per-channel role-visibility chips.
  // Reads from the channel's visible_to_roles unless the admin has
  // already touched this channel in the current session (then roleOverrides
  // is authoritative). Keyed by ChannelDef object reference so edits to
  // the id string don't break the lookup (cid df79e959).
  private _roleSetFor(card: ChannelsCard, ch: ChannelDef): Set<string> {
    const override = card.roleOverrides.get(ch);
    if (override) return override;
    return new Set<string>(Array.isArray(ch?.visible_to_roles) ? ch.visible_to_roles! : []);
  }

  isChannelRoleSelected(card: ChannelsCard, ch: ChannelDef, role: string): boolean {
    return this._roleSetFor(card, ch).has(role);
  }

  toggleChannelRole(card: ChannelsCard, ch: ChannelDef, role: string) {
    const current = this._roleSetFor(card, ch);
    const next = new Set(current);
    if (next.has(role)) next.delete(role);
    else next.add(role);
    card.roleOverrides.set(ch, next);
  }

  channelHasRoleOverride(card: ChannelsCard, ch: ChannelDef): boolean {
    return card.roleOverrides.has(ch);
  }

  cardHasRoleEdits(card: ChannelsCard): boolean {
    return card.roleOverrides.size > 0 || card.metadataDirty;
  }

  // IT-F2-406 cid df79e959 — flip the metadata-dirty flag whenever the
  // admin edits id/title/slug on any channel row. Save path reads this
  // alongside roleOverrides when deciding which scanners to PATCH.
  markMetadataDirty(card: ChannelsCard) {
    card.metadataDirty = true;
  }

  selectAll(card: ChannelsCard) {
    for (const ch of card.channels) card.selected.add(ch.id);
  }
  clearAll(card: ChannelsCard) {
    card.selected.clear();
  }

  cardSummary(card: ChannelsCard): string {
    return card.selected.size + ' / ' + card.channels.length;
  }

  cardSubtitle(card: ChannelsCard): string {
    const cname = String(card.customer.name || card.customer._id);
    return `${cname} • ${card.scannerId}`;
  }

  removeOrphan(id: string) {
    this.orphans = this.orphans.filter(x => x !== id);
  }

  get debugJson(): string {
    const payload = {
      user: {
        display_name: this.userDisplayName || null,
        email: this.userEmail || null,
        pool_id: this.userPoolId || null,
        'custom:role': this.userRole || null,
        'custom:scanners': this.userScanners || null,
        'custom:notify_channels': this.channelsJson || null,
      },
      entitled_scanners: this.cards.map(card => ({
        scanner_id: card.scannerId,
        scanner_name: card.scannerName,
        customer_id: card.customer._id,
        channels: card.channels,
        selected_ids: Array.from(card.selected).sort(),
      })),
      orphans: this.orphans,
    };
    return JSON.stringify(payload, null, 2);
  }

  openDebugModal() {
    this.debugCopied = false;
    this.showDebugModal = true;
  }

  closeDebugModal() {
    this.showDebugModal = false;
  }

  copyDebugJson() {
    try {
      navigator.clipboard?.writeText?.(this.debugJson);
      this.debugCopied = true;
      setTimeout(() => { this.debugCopied = false; }, 1500);
    } catch { /* clipboard not available (insecure context); user can select the <pre> */ }
  }

  onSave() {
    // Flatten picked ids in scanner-card declaration order across
    // cards, then append surviving orphans. Dedupe defensively even
    // though sets prevent intra-card duplicates.
    const out: string[] = [];
    const seen = new Set<string>();
    const push = (id: string) => {
      if (seen.has(id)) return;
      seen.add(id);
      out.push(id);
    };
    for (const card of this.cards) {
      for (const ch of card.channels) {
        if (card.selected.has(ch.id)) push(ch.id);
      }
    }
    for (const id of this.orphans) push(id);
    this.save.emit(out.join(','));

    // IT-F2-406 cid d97cf968 + cid df79e959 — emit per-scanner channels[]
    // updates when the admin touched role chips OR edited channel metadata
    // (id/title/slug). Each entry carries the FULL channels[] array for
    // that scanner (card.channels — a deep-copy mutated by the inline
    // inputs — merged with role overrides) so the backend PUT replaces
    // atomically rather than merging per-element.
    const scannerUpdates: ScannerChannelsUpdate[] = [];
    for (const card of this.cards) {
      const hasRoleEdits = card.roleOverrides.size > 0;
      const hasMetadataEdits = card.metadataDirty;
      if (!hasRoleEdits && !hasMetadataEdits) continue;
      const merged: ChannelDef[] = card.channels.map(def => {
        const copy: ChannelDef = { ...def };
        const override = card.roleOverrides.get(def);
        if (override) {
          const roles = Array.from(override).sort();
          // Strip visible_to_roles when empty so absent-is-visible semantics
          // round-trip cleanly (per f2-session's spec).
          if (roles.length === 0) delete copy.visible_to_roles;
          else copy.visible_to_roles = roles;
        }
        // Trim metadata strings so trailing spaces don't persist.
        if (typeof copy.id === 'string') copy.id = copy.id.trim();
        if (typeof copy.title === 'string') copy.title = copy.title.trim();
        if (typeof copy.slug === 'string') copy.slug = copy.slug.trim();
        return copy;
      });
      scannerUpdates.push({ scanner_id: card.scannerId, channels: merged });
    }
    if (scannerUpdates.length > 0) {
      this.saveScannerChannels.emit(scannerUpdates);
    }
  }

  onCancel() {
    this.close.emit();
  }
}
